define((require) => {
    "use strict";

    const $                              = require("jquery");
    const {
        defer, compose, map, flatten
    } = require("underscore");
    const Browser                        = require("shared/services/Browser");
    const Ckeditor                       = require("ckeditor");
    const CkeditorPatcher                = require("ui/experience/widgets/textWidget/textEditor/CkeditorPatcher");
    const Constants                      = require("ui/experience/widgets/textWidget/textEditor/TextEditorConstants");
    const DomHandler                     = require("ui/experience/widgets/textWidget/textEditor/DomHandler");
    const Marionette                     = require("marionette");
    const Preconditions                  = require("shared/util/Preconditions");
    const {camelize}                     = require("shared/util/StringUtils");

    const flatMap = compose(flatten, map);

    const {
        TAGS, HEADINGS, BLOCK_STYLES, INLINE_STYLES, INLINE_ATTRIBUTES
    } = Constants;

    const HIGH_LIGHT = new Ckeditor.style({
        element: TAGS.SPAN,
        attributes: {"class": Constants.HIGH_LIGHT_CLASS}
    });

    CkeditorPatcher.patch(Ckeditor);

    /**
     * Helper function to quick extract the needed css styles
     */
    function getCurrentDivCss(editor) {
        const el = editor.elementPath().contains((element) => element.is("div"));

        return BLOCK_STYLES.reduce((css, property) => {
            const camelProperty = camelize(property);

            if (el.$.style[camelProperty]) {
                css[property] = el.$.style[camelProperty];
            }

            return css;
        }, {});
    }

    function getCssValueFromData({property, value}) {
        switch (property) {
            case "font-family":
                return value.cssFamilyNames;

            case "letter-spacing":
            case "font-size":
                return `${value}px`;

            default:
                return value;
        }
    }

    function getFontAttributesFromData({value}) {
        const attributes = {
            "data-font-family": value.fontFamily,
            "data-font-provider": value.fontProvider
        };

        const {meta} = value;

        if (meta && meta.kitID) {
            attributes["data-kitid"] = meta.kitID;
        }

        if (meta && meta.projectId) {
            attributes["data-projectid"] = meta.projectId;
        }

        if (meta) {
            const {path, customFontId, format} = meta;

            if (path && customFontId && format) {
                attributes["data-path"] = path;
                attributes["data-customfontid"] = customFontId;
                attributes["data-format"] = format;
            }
        }

        return attributes;
    }

    /**
     * Configuration of CKEditor.
     */

    // Avoid automatic instantiation. We do it manually on click.
    Ckeditor.disableAutoInline = true;

    // Disable spell check, otherwise IE consumes lots of memory. see bug MOS-2185
    if (Browser.type.ie) {
        Ckeditor.config.disableNativeSpellChecker = true;
    }

    // Tell CKEditor that it has an extra plugin to load. This in itself does
    // not do anything, but without it, CK will ignore the later added plugin.
    Ckeditor.config.extraPlugins = Constants.ADDSTYLE;

    Ckeditor.config.title = false;
    // We use <div> as breaks (p tags are default) to avoid any potential
    // problems with p's default css margins.
    Ckeditor.config.enterMode = Ckeditor.ENTER_DIV;

    // Prevent filler nodes in all empty blocks. See:
    // http://docs.ckeditor.com/#!/api/CKEDITOR.config-cfg-skin
    Ckeditor.config.fillEmptyBlocks = false;

    // Avoids odd pastes from other programs.
    Ckeditor.config.forcePasteAsPlainText = true;

    // Don't load the external config.js file.
    Ckeditor.config.customConfig = false;

    // Don't load the stylesheet (we completely removed the interface).
    Ckeditor.config.stylesSet = false;

    // This is mostly for preventing loading external files (which will break
    // when compiled).
    Ckeditor.config.defaultLanguage = "en";
    Ckeditor.config.language = "en";
    // This is normally an object full of all translations, but without an
    // interface, we can get away with no translations).
    Ckeditor.lang.en = {clipboard: {}};

    // Everything that isn't included here will be stripped away when trying to
    // save or loading the editor. See:
    // http://docs.ckeditor.com/#!/guide/dev_allowed_content_rules-section-string-format
    Ckeditor.config.allowedContent = {};
    Ckeditor.config.allowedContent[TAGS.DIV] = {styles: BLOCK_STYLES.toString()};
    Ckeditor.config.allowedContent[TAGS.SPAN] = {
        attributes: INLINE_ATTRIBUTES.toString(),
        styles: INLINE_STYLES.toString()
    };
    HEADINGS.forEach((heading) => {
        Ckeditor.config.allowedContent[heading] = Ckeditor.config.allowedContent[TAGS.DIV];
    });

    /**
     * Used by CKEditor to add new styles to the selection.
     *
     * @param {Ckeditor} editor The editor in question.
     * @param {Object} data Object containing css information.
     */
    function addStyle(editor, data) {
        // Make properties for the editor
        const properties = {
            element: data.nodeName,
            styles: !data.disableFallbackStyles && data.nodeName === TAGS.DIV ? getCurrentDivCss(editor) : {}
        };

        if (data.property) {
            properties.styles[data.property] = getCssValueFromData(data);

            if (data.property === "font-family") {
                properties.attributes = getFontAttributesFromData(data);
            }
        }

        // Apply the styles.
        editor.applyStyle(new Ckeditor.style(properties));
    }

    // Telling CKEditor to use the above function.
    Ckeditor.plugins.add(Constants.ADDSTYLE, {
        init(editor) {
            editor.addCommand(Constants.ADDSTYLE, {exec: addStyle});
        }
    });

    return Marionette.Controller.extend({

        /**
         *  Raw dom element
         */
        el: null,

        /**
         * jQuery version
         */
        $el: null,

        /**
         * The instance of the editor returned by CKEditor.
         */
        _ck: null,

        elementSize: null,

        isFocused: false,

        initialize($el) {
            this.el = $el.get(0);
            this.$el = $el;
            this._ck = Ckeditor.inline(this.el);

            const changeHandler = this._changeHandler.bind(this);
            this._ck.on("focus", this._focusHandler.bind(this));
            this._ck.on("blur", this._blurHandler.bind(this));
            this._ck.on("change", changeHandler);
            this._ck.on("paste", changeHandler);
            this._ck.on("readOnly", this._readOnlyHandler.bind(this));
        },

        _changeHandler(evt) {
            const nextSize = this.el.getBoundingClientRect();
            const heightDelta = nextSize.height - this.elementSize.height;
            if (heightDelta > 0) {
                const tinyscrollbar = $(".viewport").data("tinyscrollbar");
                tinyscrollbar.y.update().scrollTo("offset", heightDelta * -1, true, true);
            }
            this.elementSize = nextSize;
            // We need to remove the Zero-Width space to not store them into the database.
            if (evt.data) {
                evt.data.dataValue = evt.data.dataValue.replace(/\u200B/g, "");
            }

            this.triggerChange();
        },

        _focusHandler() {
            this.elementSize = this.el.getBoundingClientRect();
            if (!this.isFocused) {
                const tinyscrollbar = $(".viewport").data("tinyscrollbar");
                tinyscrollbar.y.update().scrollTo("visible", this.elementSize.bottom, true, true);

                this.isFocused = true;
            }
        },

        _blurHandler() {
            this.isFocused = false;
        },

        _readOnlyHandler(evt) {
            if (evt.editor.readOnly) {
                // editor gets into read-only state, which means it has been editable until just now
                // lets propagate only this state as it is the only state we are interested so far
                this.trigger("readOnly");
            }
        },

        triggerChange() {
            defer(() => this.trigger("change"));
        },

        /**
         * Applies the passed in style directly to the selection of the edited
         * text.
         *
         * @param {Object} data - Object containing information about the style
         *                        being applied, such as property, value, and
         *                        nodeName. In the case of applying a font
         *                        family, it is assumed that fontProvider, and
         *                        possible fontMetadata is present.
         *
         *  Examples.
         *
         *      applyStyle({
         *          property: "color",
         *          value: "#ffff00",
         *          nodeName: "span"
         *      });
         *
         *
         *      applyStyle({
         *          property: "font-family",
         *          value: {
         *              fontFamily: "Proxima Nova",
         *              cssFamilyNames: "proximia-nova",
         *              fontProvider: "typekit",
         *              meta: {
         *                  kitID: "xxxxxx"
         *              }
         *          },
         *          nodeTyle: "span"
         *      });
         */
        applyStyle(data) {
            this._ck.getCommand(Constants.ADDSTYLE).setState(Ckeditor.TRISTATE_ON);

            // It seems the below "command" (execCommand(Constants.ADDSTYLE, data)) will make CKEditor
            // steal the current focus - this is not ideal if, for say, we're currently setting the
            // text color in the widget properties panel from within an input element. Therefore, let's
            // make sure to restore focus.
            const currentFocusInputElement = document.activeElement instanceof HTMLInputElement ? document.activeElement : null;

            // This will sometimes fail due to a browser bug in Chrome. The
            // error happens deep within CKEditor, so for now, we opt to go for
            // just catching the error, log it, and continue excecution.
            // The bug does not break anything.
            // See first comment in http://jira.zmags.com/browse/MOS-1445
            try {
                // TODO: This will steal the current focus - is there an official way to avoid that without a hack?
                this._ck.execCommand(Constants.ADDSTYLE, data);
            } catch (error) {
                Preconditions.fail("CKEditor error: " + error.toString());
            }

            // Restore focus if needed...
            if (currentFocusInputElement) {
                currentFocusInputElement.focus();
            }

            this._ck.getCommand(Constants.ADDSTYLE).setState(Ckeditor.TRISTATE_OFF);

            this.triggerChange();
        },

        /**
         * Similar to `applyStyle`, only that it removes the style from the
         * selection (and all children).
         *
         * @param {Object} style - Css properties and attributes that will be
         *                         removed from the elements.
         */
        removeStyle(style) {
            DomHandler.removeStyle(this.$el, style);
            this.triggerChange();
        },

        /**
         * Adds the blue highlighting of the text. This is used, so
         * even when picking properties on the right, we maintain the selection
         * visually.
         */
        applyFakeHighlight() {
            this._ck.applyStyle(HIGH_LIGHT);
        },

        removeFakeHighlight() {
            this._ck.removeStyle(HIGH_LIGHT);
        },

        selectAll() {
            defer(() => {
                this._ck.element.focus();
                const selection = this._ck.getSelection();
                selection.selectElement(this._ck.element);

                const range = this._ck.createRange();
                range.moveToElementEditStart(range.root);
                range.selectNodeContents(this._ck.element);

                selection.selectRanges([range]);
            });
        },

        deselectAll() {
            const selection = this._ck.getSelection();
            selection.removeAllRanges();
        },

        getSelectedElement() {
            const ranges = this._ck.getSelection().getRanges();

            return flatMap(ranges, (range) => {
                const startNode       = range.startContainer.$;
                const endNode         = range.endContainer.$;
                const commonAncestor  = range.getCommonAncestor() && range.getCommonAncestor().$;

                return DomHandler.getElementsBetweenNodes(startNode, endNode, commonAncestor);
            });
        },

        /**
         * setIsEditable. Toggles the editable state of the editor. If this is
         * set to `false`, then applying or removing styles, as well as adding
         * or removing fake highlighting won't have any effect.
         *
         * @param {Boolean} isEditable - true: Editing enabled.
         *                               false: Editing impossible.
         */
        setIsEditable(isEditable) {
            this._ck.setReadOnly(!isEditable);
        },

        /**
         * getSelectedText - Returns the selected text.
         *
         * @return {String} - The selected text.
         */
        getSelectedText() {
            const selection = this._ck.getSelection();

            return selection && selection.getSelectedText();
        },

        /**
         * getSpanTouchingCursor. Returns the element closest to the cursor in
         * the editor. If there is no cursor, but rather a selection, it will
         * return the element nearest to the beginning of the selection.
         *
         * @return {HTMLElement}
         */
        getElementTouchingCursor() {
            const elementPath = this._ck.elementPath();

            return elementPath ? elementPath.elements[0].$ : null;
        },

        onClose() {
            // We need defer here because CKEditor has a timeout call when it initializes an editor instance.
            // If initialization happens very fast like changing scene size, then we may end up having some
            // uninitialized object and calling methods against that object will throw errors. See the bug MOS-1821.
            defer(this._ck.destroy.bind(this._ck));
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/textWidget/textEditor/CkeditorFacade.js